import React from "react";
import { Tooltip } from "@material-ui/core";
import styled from "styled-components";
import { ResizeHandle } from "./ResizeHandle";
import { FrameAdjuster } from "./FrameAdjuster";
import { theme } from "./theme";
import { hexToRgba } from "../utils/hexToRgba";
import IncidentType from "types/IncidentType";

interface IntervalItemStyledProps {
  editing?: boolean;
}

const IntervalItem = styled.div`
  position: absolute;
  cursor: pointer;
  ::before {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    box-sizing: border-box;
    border-radius: 8px;
    content: "";
    z-index: 9;
    border: ${(props: IntervalItemStyledProps) =>
      props.editing ? "2px solid rgb(25, 118, 210)" : "none"};
  }
  &:hover::before {
    border: 2px solid rgb(25, 118, 210);
  }
`;

export interface IncidentIntervalItemProps {
  incident: IncidentType;
  clientWidth: number;
  totalFrames: number;
  currentFrame: number;
  isEditing: boolean;
  isDragging: boolean;
  showLabel?: boolean;
  onSelect: (e: React.MouseEvent) => void;
  onSeekToStart: (e: React.MouseEvent) => void;
  onResizeMouseDown: (e: React.MouseEvent, isLeft: boolean) => void;
  onAdjustFrame: (isLeft: boolean, increment: boolean) => void;
}

const IncidentIntervalItem: React.FC<IncidentIntervalItemProps> = ({
  incident,
  clientWidth,
  totalFrames,
  currentFrame,
  isEditing,
  isDragging,
  showLabel = true,
  onSelect,
  onSeekToStart,
  onResizeMouseDown,
  onAdjustFrame,
}) => {
  const color = hexToRgba(incident.class?.color || theme.colors.background, 0.6);
  const endFrame = incident.endFrame ?? currentFrame;
  const width =
    totalFrames > 0 ? ((endFrame - incident.startFrame) * clientWidth) / totalFrames : 0;
  const left = totalFrames > 0 ? (incident.startFrame * clientWidth) / totalFrames : 0;
  const title = incident.class?.name ?? "";

  return (
    <div style={{ position: "relative" }}>
      <Tooltip title={title}>
        <IntervalItem
          editing={isEditing}
          style={{
            left,
            backgroundColor: color,
            borderRadius: theme.borderRadius.medium,
            width,
            height: theme.sizes.handleHeight,
          }}
          onClick={onSelect}
          onDoubleClick={onSeekToStart}
        >
          {showLabel && (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                height: "100%",
                alignItems: "center",
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              <p
                style={{
                  marginLeft: isEditing ? "12px" : "4px",
                  textOverflow: "ellipsis",
                  color: incident.class?.color,
                  fontSize: "0.75em",
                  textShadow: "#FFF 1px 0 10px",
                  fontWeight: "bold",
                  filter: "brightness(0.7)",
                  userSelect: "none",
                }}
              >
                {incident.class?.name}
              </p>
            </div>
          )}
          {isEditing && (
            <>
              <ResizeHandle
                position="left"
                framePosition={incident.startFrame}
                totalFrames={totalFrames}
                clientWidth={clientWidth}
                onMouseDown={(e) => onResizeMouseDown(e, true)}
                isDragging={isDragging}
              />
              <ResizeHandle
                position="right"
                framePosition={endFrame}
                totalFrames={totalFrames}
                clientWidth={clientWidth}
                onMouseDown={(e) => onResizeMouseDown(e, false)}
                isDragging={isDragging}
              />
            </>
          )}
        </IntervalItem>
      </Tooltip>
      {isEditing && (
        <>
          <FrameAdjuster
            framePosition={incident.startFrame}
            totalFrames={totalFrames}
            clientWidth={clientWidth}
            onAdjustFrame={(increment) => onAdjustFrame(true, increment)}
          />
          <FrameAdjuster
            framePosition={endFrame}
            totalFrames={totalFrames}
            clientWidth={clientWidth}
            onAdjustFrame={(increment) => onAdjustFrame(false, increment)}
            isTop={width < 35}
          />
        </>
      )}
    </div>
  );
};

export default IncidentIntervalItem;
